import { useState, useEffect } from 'react';
import { Text, Card, Button } from '@hawkins/components';
import NetflixGradientBackground from 'src/components/elements/NetflixGradientBackground';

interface MfaState {
  authorizationUrl?: string;
  state?: string;
  codeChallenge?: string;
  redirectUri?: string;
  clientId?: string;
  environment?: string;
}

interface TokenExchangeResult {
  success: boolean;
  identitiesMatch?: boolean;
  mfaUserId?: string;
  resetUserId?: string;
  resetUser?: {
    username: string;
    domain: string;
    fullName: string;
  };
  tokenInfo?: {
    hasAccessToken: boolean;
    hasIdToken: boolean;
    tokenType: string;
    expiresIn: number;
  };
  mfaCompleted?: boolean;
  error?: string;
  details?: string;
}

const MfaConfigDisplay = ({ mfaState }: { mfaState?: MfaState }) => (
  <Card style={{
    backgroundColor: '#1a1a2e',
    border: '1px solid #16213e',
    borderRadius: '4px',
    padding: '20px',
    marginBottom: '20px'
  }}>
    <Text style={{ color: '#e94560', fontSize: '16px', marginBottom: '16px' }}>
      🔐 Step-up MFA Configuration {mfaState?.environment && `(Environment: ${mfaState.environment})`}
    </Text>

    <div style={{ marginBottom: '20px' }}>
      <Text style={{ color: '#f5f5f5', fontSize: '14px', marginBottom: '12px' }}>
        OAuth2 Parameters:
      </Text>
      <div style={{ paddingLeft: '16px' }}>
        <div style={{ marginBottom: '8px' }}>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            Client ID:
          </Text>
          <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
            {mfaState?.clientId || 'Loading...'}
          </Text>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            Redirect URI:
          </Text>
          <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px', wordBreak: 'break-all' }}>
            {mfaState?.redirectUri || 'Loading...'}
          </Text>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            State:
          </Text>
          <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
            {mfaState?.state || 'Not generated'}
          </Text>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            Code Challenge:
          </Text>
          <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
            {mfaState?.codeChallenge || 'Not generated'}
          </Text>
        </div>
      </div>
    </div>

    {mfaState?.authorizationUrl && (
      <div style={{ marginBottom: '20px' }}>
        <Text style={{ color: '#f5f5f5', fontSize: '14px', marginBottom: '12px' }}>
          Authorization URL:
        </Text>
        <div style={{
          paddingLeft: '16px',
          backgroundColor: '#0f0f1e',
          padding: '10px',
          borderRadius: '4px',
          border: '1px solid #2d2d44'
        }}>
          <Text style={{
            color: '#3282b8',
            fontSize: '12px',
            fontFamily: 'monospace',
            wordBreak: 'break-all',
            lineHeight: '1.4'
          }}>
            {mfaState.authorizationUrl}
          </Text>
        </div>
      </div>
    )}
  </Card>
);

const AuthorizationCodeDisplay = ({ code, error }: { code?: string, error?: string }) => {
  if (!code && !error) return null;

  return (
    <Card style={{
      backgroundColor: error ? '#2e1a1a' : '#1a2e1a',
      border: error ? '1px solid #3e1616' : '1px solid #163e16',
      borderRadius: '4px',
      padding: '20px',
      marginBottom: '20px'
    }}>
      <Text style={{ color: error ? '#e94560' : '#60e945', fontSize: '16px', marginBottom: '16px' }}>
        {error ? '❌ MFA Error' : '✅ Authorization Code Received'}
      </Text>

      <div style={{
        backgroundColor: '#0f0f1e',
        padding: '12px',
        borderRadius: '4px',
        border: '1px solid #2d2d44'
      }}>
        <Text style={{
          color: error ? '#ff6b6b' : '#51cf66',
          fontSize: '14px',
          fontFamily: 'monospace',
          wordBreak: 'break-all',
          lineHeight: '1.4'
        }}>
          {error || code}
        </Text>
      </div>
    </Card>
  );
};

const TokenExchangeDisplay = ({ result, loading }: { result?: TokenExchangeResult | null, loading?: boolean }) => {
  if (loading) {
    return (
      <Card style={{
        backgroundColor: '#1a1a2e',
        border: '1px solid #16213e',
        borderRadius: '4px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <Text style={{ color: '#3282b8', fontSize: '16px', marginBottom: '16px' }}>
          🔄 Processing Token Exchange...
        </Text>
      </Card>
    );
  }

  if (!result) return null;

  const isSuccess = result.success && result.identitiesMatch;
  const bgColor = result.success ? (isSuccess ? '#1a2e1a' : '#2e2e1a') : '#2e1a1a';
  const borderColor = result.success ? (isSuccess ? '#163e16' : '#3e3e16') : '#3e1616';
  const iconColor = result.success ? (isSuccess ? '#60e945' : '#e9c545') : '#e94560';
  const icon = result.success ? (isSuccess ? '✅' : '⚠️') : '❌';

  return (
    <Card style={{
      backgroundColor: bgColor,
      border: `1px solid ${borderColor}`,
      borderRadius: '4px',
      padding: '20px',
      marginBottom: '20px'
    }}>
      <Text style={{ color: iconColor, fontSize: '16px', marginBottom: '16px' }}>
        {icon} {isSuccess ? 'MFA Validation Successful - Signature Verified' : result.success ? 'Token Exchange Successful - Identity Mismatch' : 'Token Exchange Failed - Signature Invalid'}
      </Text>

      {result.success && (
        <div style={{ marginBottom: '16px' }}>
          <Text style={{ color: '#f5f5f5', fontSize: '14px', marginBottom: '8px' }}>
            Identity Validation:
          </Text>
          <div style={{ paddingLeft: '16px' }}>
            <div style={{ marginBottom: '8px' }}>
              <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
                MFA User ID:
              </Text>
              <Text style={{ color: isSuccess ? '#51cf66' : '#ffa726', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
                {result.mfaUserId}
              </Text>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
                Reset User ID:
              </Text>
              <Text style={{ color: isSuccess ? '#51cf66' : '#ffa726', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
                {result.resetUserId}
              </Text>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
                Identities Match:
              </Text>
              <Text style={{ color: isSuccess ? '#51cf66' : '#ff6b6b', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
                {result.identitiesMatch ? 'YES' : 'NO'}
              </Text>
            </div>
          </div>
        </div>
      )}

      {result.resetUser && (
        <div style={{ marginBottom: '16px' }}>
          <Text style={{ color: '#f5f5f5', fontSize: '14px', marginBottom: '8px' }}>
            Reset User Details:
          </Text>
          <div style={{ paddingLeft: '16px' }}>
            <div style={{ marginBottom: '4px' }}>
              <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
                Username: {result.resetUser.username}
              </Text>
            </div>
            <div style={{ marginBottom: '4px' }}>
              <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
                Domain: {result.resetUser.domain}
              </Text>
            </div>
            <div>
              <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
                Full Name: {result.resetUser.fullName}
              </Text>
            </div>
          </div>
        </div>
      )}

      {result.tokenInfo && (
        <div style={{ marginBottom: '16px' }}>
          <Text style={{ color: '#f5f5f5', fontSize: '14px', marginBottom: '8px' }}>
            Token Information:
          </Text>
          <div style={{ paddingLeft: '16px' }}>
            <div style={{ marginBottom: '4px' }}>
              <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
                Has Access Token: {result.tokenInfo.hasAccessToken ? 'Yes' : 'No'}
              </Text>
            </div>
            <div style={{ marginBottom: '4px' }}>
              <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
                Has ID Token: {result.tokenInfo.hasIdToken ? 'Yes' : 'No'}
              </Text>
            </div>
            <div>
              <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
                Expires In: {result.tokenInfo.expiresIn} seconds
              </Text>
            </div>
          </div>
        </div>
      )}

      {result.error && (
        <div style={{
          backgroundColor: '#0f0f1e',
          padding: '12px',
          borderRadius: '4px',
          border: '1px solid #2d2d44'
        }}>
          <Text style={{
            color: '#ff6b6b',
            fontSize: '14px',
            fontFamily: 'monospace',
            wordBreak: 'break-all',
            lineHeight: '1.4'
          }}>
            Error: {result.error}
            {result.details && <><br />Details: {result.details}</>}
          </Text>
        </div>
      )}
    </Card>
  );
};

const NetflixLogo = () => (
  <div style={{ marginBottom: '40px' }}>
    <img
      src="/nflx-logo-classic@150w.png"
      alt="Netflix"
      style={{ height: '80px' }}
    />
  </div>
);

// Note: PKCE generation now handled securely by backend in /api/mfa/initiate

function MfaDebugPage() {
  const [mfaState, setMfaState] = useState<MfaState>({});
  const [authCode, setAuthCode] = useState<string>('');
  const [authError, setAuthError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [tokenExchangeResult, setTokenExchangeResult] = useState<TokenExchangeResult | null>(null);
  const [tokenExchangeLoading, setTokenExchangeLoading] = useState(false);
  const [resetToken, setResetToken] = useState<string>('');

  // Fetch environment info on component mount
  useEffect(() => {
    const fetchEnvironmentInfo = async () => {
      try {
        const response = await fetch('/api/debug/environment-info');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.environmentInfo) {
            setMfaState(prev => ({
              ...prev,
              environment: data.environmentInfo.environment
            }));
          }
        }
      } catch (error) {
        console.error('Failed to fetch environment info:', error);
      }
    };

    fetchEnvironmentInfo();
  }, []);

  useEffect(() => {
    // Check URL for OAuth callback parameters
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (code && state) {
      setAuthCode(code);
      // Clean URL without triggering navigation
      window.history.replaceState({}, document.title, window.location.pathname);

      // Automatically exchange tokens
      exchangeTokens(code, state);
    } else if (error) {
      setAuthError(errorDescription || error);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Initialize MFA configuration
    initializeMfaConfig();
  }, []);

  const initializeMfaConfig = () => {
    const baseUrl = window.location.origin;
    const redirectUri = `${baseUrl}/mfa/debug`;

    const clientId = '0oapyoyt2gwaZI06A1d7';

    setMfaState({
      clientId,
      redirectUri
    });
  };

  const exchangeTokens = async (authorizationCode: string, state: string) => {
    setTokenExchangeLoading(true);
    setTokenExchangeResult(null);

    try {

      const redirectUri = `${window.location.origin}/mfa/debug`;

      const response = await fetch('/api/mfa/exchange-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          authorizationCode,
          state,
          redirectUri
        }),
        credentials: 'include' // Include cookies
      });

      const result = await response.json();

      setTokenExchangeResult(result);
    } catch (error) {
      console.error('Token exchange request failed:', error);
      setTokenExchangeResult({
        success: false,
        error: 'Failed to exchange tokens',
        details: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setTokenExchangeLoading(false);
    }
  };

  const initiateStepUpMfa = async () => {
    setLoading(true);
    setAuthCode('');
    setAuthError('');
    setTokenExchangeResult(null);

    try {

      // Use provided reset token or dummy for testing
      const tokenToUse = resetToken.trim() || 'dummy-reset-token-for-testing';
      const redirectUri = `${window.location.origin}/mfa/debug`;

      // Call backend to generate secure OAuth URL
      const response = await fetch('/api/mfa/initiate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resetToken: tokenToUse,
          redirectUri: redirectUri
        }),
        credentials: 'include' // Include cookies for secure storage
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'MFA initiation failed');
      }

      const { authorizationUrl, environment } = result;

      // Update state for display
      setMfaState(prev => ({
        ...prev,
        authorizationUrl,
        clientId: '0oapyoyt2gwaZI06A1d7', // For display only
        redirectUri,
        environment
      }));

      // Redirect to IDP with backend-generated secure URL
      window.location.href = authorizationUrl;

    } catch (error) {
      console.error('Error initiating MFA:', error);
      setAuthError(`Failed to initiate MFA: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <NetflixGradientBackground>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        paddingTop: '40px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: '600px'
        }}>
          <NetflixLogo />

          <Card style={{
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            padding: '48px 68px 36px',
            borderRadius: '4px',
            width: '100%',
            border: 'none'
          }}>
            <Text
              style={{
                color: 'white',
                fontSize: '28px',
                fontWeight: '700',
                marginBottom: '24px',
                textAlign: 'center'
              }}
            >
              Step-up MFA Debug
            </Text>

            <MfaConfigDisplay mfaState={mfaState} />

            {/* Reset Token Input */}
            <Card style={{
              backgroundColor: '#1a1a2e',
              border: '1px solid #16213e',
              borderRadius: '4px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <Text style={{ color: '#e94560', fontSize: '16px', marginBottom: '16px' }}>
                🔑 Reset Token Configuration
              </Text>

              <Text style={{ color: '#f5f5f5', fontSize: '14px', marginBottom: '8px' }}>
                Reset Token (optional - uses dummy token if empty):
              </Text>

              <input
                type="text"
                value={resetToken}
                onChange={(e) => setResetToken(e.target.value)}
                placeholder="Enter reset token from password reset email..."
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#0f0f1e',
                  border: '1px solid #2d2d44',
                  borderRadius: '4px',
                  color: '#f5f5f5',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  marginBottom: '8px',
                  boxSizing: 'border-box'
                }}
              />

              <Text style={{
                color: '#b3b3b3',
                fontSize: '12px',
                lineHeight: '1.4'
              }}>
                To get a reset token: Go to the main app (/) → Request password reset → Copy token from email URL
              </Text>
            </Card>

            <AuthorizationCodeDisplay code={authCode} error={authError} />

            <TokenExchangeDisplay result={tokenExchangeResult} loading={tokenExchangeLoading} />

            <Button
              onClick={initiateStepUpMfa}
              disabled={loading}
              style={{
                width: '100%',
                backgroundColor: '#e50914',
                border: 'none',
                borderRadius: '4px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '700',
                padding: '12px 16px',
                cursor: 'pointer',
                height: '48px',
                minHeight: '48px',
                boxSizing: 'border-box',
                marginBottom: '12px'
              }}
            >
              {loading ? 'Redirecting to MFA...' : 'Test Step-up MFA'}
            </Button>

            <Text style={{
              color: '#b3b3b3',
              fontSize: '14px',
              textAlign: 'center',
              lineHeight: '1.4'
            }}>
              This will redirect you to the Netflix IDP for step-up authentication.
              After authentication, you'll return here with an authorization code.
            </Text>
          </Card>
        </div>
      </div>
    </NetflixGradientBackground>
  );
}

export default MfaDebugPage;
